import unitsJson from './generated/units.json' with { type: 'json' };
import techsJson from './generated/techs.json' with { type: 'json' };
import worldJson from './generated/world.json' with { type: 'json' };
import specialistsJson from './generated/specialists.json' with { type: 'json' };
import packsJson from './generated/starter_packs.json' with { type: 'json' };
import unlockJson from './generated/unlock.json' with { type: 'json' };
import towersJson from './generated/towers.json' with { type: 'json' };
import devicesJson from './generated/devices.json' with { type: 'json' };
import economyJson from './generated/economy.json' with { type: 'json' };
import cardsJson from './generated/cards.json' with { type: 'json' };
import equipmentJson from './generated/equipment.json' with { type: 'json' };
/** Source client build the data is extracted from (see gamedoc/README.md). */
export const GAME_VERSION = '1.11.1.3.2259';
/**
 * Bumped whenever the shape of the generated JSON changes.
 *  2: P1 extras — specialists, packs, unlock, towers, devices, economy, cards
 *  3: P2 effect model — techs carry perRank / ability / locked / equipped (206 rows from client_ground_truth v2+),
 *     specialists.modifiers, towers.towerSkills + purchase modifiers/ability, devices.ability, equipment.json,
 *     cards[equipment].effects. All additive.
 */
export const DATA_SCHEMA_VERSION = 3;
const unitsFile = unitsJson;
const techsFile = techsJson;
export const UNITS_META = unitsFile._meta;
export const TECHS_META = techsFile._meta;
/** All 32 unit types keyed by config unit id (1..31 and 2002), with provenance and data-package extras. */
export const UNIT_RECORDS = unitsFile.units;
/** The same records typed as the sim contract, for FightInput.unitTypes. */
export const UNIT_TYPES = UNIT_RECORDS;
/** Unit ids in ascending order. */
export const UNIT_IDS = Object.keys(UNIT_RECORDS)
    .map(Number)
    .sort((a, b) => a - b);
/** Every technology row captured from the client (a lower bound of the pool: the tech list scrolls). */
export const TECHS = techsFile.techs;
/** Technologies grouped by unit id, in ascending tech-id order. */
export const TECHS_BY_UNIT = (() => {
    const out = {};
    for (const t of TECHS)
        (out[t.unit] ??= []).push(t);
    return out;
})();
export const WORLD = worldJson;
const byName = new Map();
for (const u of Object.values(UNIT_RECORDS)) {
    byName.set(u.nameEn.toLowerCase(), u);
    byName.set(u.nameRu.toLowerCase(), u);
}
/** Look a unit up by its English or Russian client name (case-insensitive). */
export function unitByName(name) {
    return byName.get(name.trim().toLowerCase());
}
export function unitById(id) {
    return UNIT_RECORDS[id];
}
export function techById(id) {
    return TECHS.find((t) => t.id === id);
}
const specialistsFile = specialistsJson;
const packsFile = packsJson;
export const SPECIALISTS_DATA = specialistsFile;
/** The 16 OfficerData rows with scope = Opening, in ascending id order. */
export const SPECIALISTS = specialistsFile.specialists;
/** The 29 shipped AdvanceTeamData rows (3 × 100-cost squads + 2 × 200-cost squads each). */
export const STARTER_PACKS = packsFile.packs;
export const UNLOCK = unlockJson;
export const TOWERS = towersJson;
export const DEVICES_DATA = devicesJson;
export const DEVICES = DEVICES_DATA.devices;
/** The 38 EquipmentData rows (52 §2) as channels / abilities; 36 carry an effect, 13030008 / 13030009 have unknown magnitudes. */
export const EQUIPMENT_DATA = equipmentJson;
export const EQUIPMENT = EQUIPMENT_DATA.equipment;
/** Command Center (per round) and Research Center (permanent, tiers I/II) skills in the uniform effect model. */
export const TOWER_SKILLS = towersJson.towerSkills;
export const ECONOMY = economyJson;
export const CARDS_DATA = cardsJson;
/** Every catalogued reinforcement card except the 1 656 unit drops (see CARDS_DATA.unitDrops and decodeUnitDropId). */
export const CARDS = CARDS_DATA.cards;
/** MatchSetting.reactorCores[0] in ranked / practice 1v1; also the max HP (the bar can start above 100 %). */
export const BASE_REACTOR_CORE = SPECIALISTS_DATA.baseHp;
export function specialistById(id) {
    return SPECIALISTS.find((s) => s.id === id);
}
export function starterPackById(id) {
    return STARTER_PACKS.find((p) => p.id === id);
}
export function cardById(id, category) {
    return CARDS.find((c) => c.id === id && (category === undefined || c.category === category));
}
export function deviceById(id) {
    return DEVICES.find((d) => d.id === id);
}
export function equipmentById(id) {
    return EQUIPMENT.find((e) => e.id === id);
}
export function towerSkillById(tower, id) {
    return TOWER_SKILLS.find((s) => s.tower === tower && s.id === id);
}
/** Initial reactor core of a start card: 4500 + specialist.reactorCore + pack.reactorCore (51 §0, 26/26 cards exact). */
export function startingHp(specialistId, packId) {
    const s = specialistById(specialistId);
    const p = starterPackById(packId);
    if (!s)
        throw new Error(`unknown specialist ${specialistId}`);
    if (!p)
        throw new Error(`unknown starter pack ${packId}`);
    return BASE_REACTOR_CORE + s.hpModifier + p.hpModifier;
}
/** Base round income before officer bonuses: min(200 × round, 4000) (40 §1.1). */
export function roundIncome(round) {
    const e = ECONOMY;
    return Math.min(e.firstRoundSupply + (round - 1) * e.roundSupplyIncrease, e.maxRoundSupply);
}
/** Skip reward of the reinforcement screen: 50 for a standard offer, 50 × (round − 1) for a unit-drop offer (40 §1.4). */
export function skipReward(round, isUnitDropOffer) {
    return isUnitDropOffer ? ECONOMY.cards.skipRewardStandard * (round - 1) : ECONOMY.cards.skipRewardStandard;
}
/** Escalated technology price: base + 200 × techs already bought on that unit type (40 §2.5). */
export function techPrice(base, techsAlreadyBoughtOnType, discount = 0) {
    return Math.max(0, base + ECONOMY.tech.escalationPerBought * techsAlreadyBoughtOnType + discount);
}
/** Rank 1 -> 2 promotion cost: 50 % of the type's base cost (40 §2.4). Higher ranks are UNKNOWN; the same rule is assumed. */
export function rankUpCost(unitBaseCost) {
    return Math.trunc((unitBaseCost * ECONOMY.rankUp.firstRankPermilleOfBase) / 1000);
}
/** Unit price at displayed rank `rank` (Elite Recruitment / Elite Specialist): base + (rank − 1) × rankUpCost (40 §2.4, INFERRED beyond rank 2). */
export function recruitPriceAtRank(unitBaseCost, rank) {
    return unitBaseCost + (rank - 1) * rankUpCost(unitBaseCost);
}
/** Decode a UnitReinforceData id: S RR C L U (50 §9.6, 1 656/1 656 rows). Returns undefined for a malformed id. */
export function decodeUnitDropId(id) {
    const s = String(id);
    if (s.length < 6)
        return undefined;
    const scene = Number(s[0]);
    const round = Number(s.slice(1, 3));
    const count = Number(s[3]);
    const rank = Number(s[4]);
    const unitId = Number(s.slice(5));
    if (!(scene >= 1 && round >= 1 && count >= 1 && rank >= 1 && unitId >= 1) || s[5] === '0')
        return undefined;
    return { scene, round, count, rank, unitId };
}
export function encodeUnitDropId(d) {
    return Number(`${d.scene}${String(d.round).padStart(2, '0')}${d.count}${d.rank}${d.unitId}`);
}
/** The scene-1 unit-drop pool of a round: every (count, rank, unitId) card that can be offered (50 §9.6). */
export function unitDropPool(round) {
    const row = CARDS_DATA.unitDrops.pool.find((p) => p.round === round);
    if (!row)
        return [];
    const out = [];
    for (const cell of row.cells)
        for (const unitId of cell.unitIds)
            out.push({ scene: 1, round, count: cell.count, rank: cell.rank, unitId });
    return out;
}
// P2 fight feed (fightEffects.ts): TECH_EFFECTS / EQUIPMENT_EFFECTS tables, scoped seat modifiers
export * from './fightEffects.js';
//# sourceMappingURL=index.js.map